import { LocalStorageDriver } from "./LocalStorageDriver";
import { PersistSchemaV3, PersistedStateV3 } from "./PersistSchema";

const STORAGE_KEY = "paws_quiz_v2";
const OLD_STORAGE_KEY = "paws_quiz_v1";

const driver = new LocalStorageDriver<PersistedStateV3>(STORAGE_KEY);
const oldDriver = new LocalStorageDriver<any>(OLD_STORAGE_KEY);

function createEmptyDogForm(): any {
  return {
    name: "",
    breed: "",

    sex: "", // NOT "male"
    years: "", // keep as "" so your step logic can treat it as empty
    months: "",
    weightKg: "",

    bodyCondition: "", // NOT "ideal"
    reproduction: "",

    allergies: [],
    healthIssues: [],

    activityLevel: "", // let user choose

    treatFrequency: "",
    getsTreats: false,
  };
}

function createInitialState(): PersistedStateV3 {
  const dogId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `dog-${Date.now()}`;

  return {
    version: 2,
    dogOrder: [dogId],
    dogForms: {
      [dogId]: createEmptyDogForm(),
    },
    activeDogId: dogId,
    stepState: {
      perDogStep: {
        [dogId]: "basic",
      },
      globalStep: "basic",
    },
    treatSelections: {},
    resultsStep: 1,
  };
}

export function hydrateQuiz(): PersistedStateV3 {
  // Try to load from v2 first
  let raw = driver.load();

  // ✅ MIGRATION: If no v2 data, try to migrate from v1
  if (!raw) {
    const oldRaw = oldDriver.load();
    if (oldRaw) {
      console.log('[Migration] Migrating quiz data from v1 to v2');
      // Migrate v1 data to v2 format (just update version field)
      raw = {
        ...oldRaw,
        version: 2,
      };
      // Save as v2
      driver.save(raw!);
      // Remove old v1 data
      try {
        localStorage.removeItem(OLD_STORAGE_KEY);
      } catch (e) {
        console.warn('Failed to remove old v1 data', e);
      }
    }
  }

  if (!raw) {
    const fresh = createInitialState();
    driver.save(fresh);
    return fresh;
  }

  const parsed = PersistSchemaV3.safeParse(raw);
  if (!parsed.success) {
    console.warn("Invalid stored quiz, resetting:", parsed.error);
    const fresh = createInitialState();
    driver.save(fresh);
    return fresh;
  }

  let state = parsed.data;

  if (!state.dogOrder.length) {
    const fresh = createInitialState();
    driver.save(fresh);
    return fresh;
  }

  // 🔥 Merge defaults with stored forms
  const mergedDogForms: Record<string, any> = {};
  for (const id of state.dogOrder) {
    const stored = state.dogForms[id] || {};
    mergedDogForms[id] = {
      ...createEmptyDogForm(),
      ...stored,
    };
  }

  const fixed: PersistedStateV3 = {
    ...state,
    dogForms: mergedDogForms,
    activeDogId: state.activeDogId ?? state.dogOrder[0],
    stepState: {
      globalStep: state.stepState.globalStep ?? "basic",
      perDogStep: {
        ...state.dogOrder.reduce(
          (acc, id) => ({
            ...acc,
            [id]: state.stepState.perDogStep[id] ?? "basic",
          }),
          {}
        ),
      },
    },
    resultsStep: state.resultsStep || 1,
  };

  driver.save(fixed);
  return fixed;
}
